import React, { FC, ReactNode } from 'react'
import classNames from 'classnames'
import Tooltip from 'components/base/Tooltip'
import Input, { InputType } from './Input'
import Icon, { IconName } from 'project/Icon'
import { GUID } from 'common/utils/string/GUID'

export interface InputGroupProps extends Omit<InputType, 'title'> {
  action?: ReactNode
  className?: string
  component?: ReactNode
  defaultValue?: string
  error?: string
  errorMessage?: string
  id?: string
  inputProps?: InputType
  isValid?: boolean
  onBlur?: InputType['onBlur']
  placeholder?: string
  required?: boolean
  textarea?: boolean
  title?: ReactNode
  tooltip?: string
  touched?: boolean
  // Legacy icon props support
  iconColour?: string
  icon?: IconName | ReactNode
  iconLeft?: ReactNode
}

const InputGroup: FC<InputGroupProps> = ({
  action,
  className,
  component,
  defaultValue,
  disabled,
  error,
  errorMessage,
  icon,
  iconColour,
  iconLeft,
  id: providedId,
  inputProps = {},
  inputRef,
  isValid,
  name,
  onBlur,
  onChange,
  placeholder,
  required,
  textarea,
  title,
  tooltip,
  touched,
  type = 'text',
  value,
}) => {
  const id = providedId || name || inputProps.id || inputProps.name || GUID()

  // Compute conditional isValid and errorMessage based on touched and error
  const computedIsValid =
    isValid !== undefined ? isValid : touched && error ? false : undefined
  const computedErrorMessage =
    errorMessage !== undefined
      ? errorMessage
      : touched && error
      ? error
      : undefined

  return (
    <div className={classNames('form-group', className)}>
      {(title || action) && (
        <div className='flex-row'>
          <Tooltip place='top' content={tooltip}>
            <label
              className='form-label d-flex align-items-center fw-semibold flex-fill'
              htmlFor={id}
            >
              {title}
              {tooltip && (
                <Icon
                  width={18}
                  fill='dark300'
                  name='info'
                  className='mx-2 text-dark300'
                />
              )}
              {required && <span className='text-danger'>*</span>}
            </label>
            {action}
          </Tooltip>
        </div>
      )}

      {component ? (
        component
      ) : (
        <Input
          name={name}
          touched={touched}
          textarea={textarea}
          inputRef={inputRef}
          onBlur={onBlur}
          {...inputProps}
          isValid={computedIsValid}
          disabled={disabled}
          value={value}
          data-test={(inputProps as any)['data-test']}
          defaultValue={defaultValue}
          onChange={onChange}
          type={type || 'text'}
          id={id}
          placeholder={placeholder}
          iconLeft={
            iconLeft ||
            (icon ? (
              <Icon fill={iconColour as any} name={icon as IconName} />
            ) : undefined)
          }
          iconLeftColour={iconColour}
        />
      )}
      {computedErrorMessage && (
        <span
          id={inputProps.name ? `${inputProps.name}-error` : ''}
          className='text-danger'
        >
          {computedErrorMessage}
        </span>
      )}
    </div>
  )
}

InputGroup.displayName = 'InputGroup'
export default InputGroup
